import {
  useMutation,
  type UseMutationOptions,
  useQueryClient,
} from '@tanstack/react-query';
import { z } from 'zod';

import { ENDPOINT } from '~/constants/endpoints';

import { type TimestampUTC, type UUID } from '~/types/common';

import { vestigasApi } from '~/services/kyClient';

import { withoutObjectKeysWhereValueIs } from '@vestigas/utils';
import { log } from '~/utils';

import {
  AI_MODEL_CHOICE_OPTIONS,
  LANGUAGE_ENCODING_OPTIONS,
  LVLM_PERFORMANCE_MODES,
  LVLM_USAGE,
  PROCESS_BULK_OPTIONS,
} from './constants';
import { queryKeysInvoiceAiModel } from './queryKeys';
import { type AiModelSettings } from './useQueryInvoiceAiModelSettings';
import { zodAiModelEntrySchema } from './validations';

const zodUpdateInvoiceAiModelSettingsPayloadSchema = z
  .object({
    aiModels: z.union([
      z.array(zodAiModelEntrySchema).min(1),
      z
        .string()
        .trim()
        .min(1)
        .transform((value) => [{ modelId: value, modelType: 'di' as const }]),
    ]),
    diPlusAiFoundryFieldsToReplace: z.array(z.string()).optional().default([]),
    languageEncoding: z.enum(LANGUAGE_ENCODING_OPTIONS).nullable().optional(),
    lvlmPerformanceMode: z.enum(LVLM_PERFORMANCE_MODES).optional(),
    lvlmUsage: z.enum(LVLM_USAGE).optional(),
    modelChoice: z.enum(AI_MODEL_CHOICE_OPTIONS).nullable().optional(),
    processBulk: z.enum(PROCESS_BULK_OPTIONS).nullable().optional(),
  })
  .transform((data) => {
    if (data?.lvlmUsage === 'false') {
      // Remove LVLM performance mode if LVLM is not used
      data.lvlmPerformanceMode = undefined;
    }

    return withoutObjectKeysWhereValueIs(data, [null, undefined, '']);
  });

type UpdateInvoiceAiModelSettingsPayload = z.infer<
  typeof zodUpdateInvoiceAiModelSettingsPayloadSchema
>;

type UpdateInvoiceAiModelSettingsResponse = {
  id: UUID;
  aiModelSettings: AiModelSettings;
  updatedOn: TimestampUTC;
  modifiedOn: TimestampUTC | undefined;
};

/**
 * Updates the invoice AI model settings with a given AI model settings ID.
 *
 * @param invoiceAiModelSettingsId - The ID of the invoice AI model settings to update.
 * @param invoiceAiModelSettings - An object containing the AI model settings to be updated.
 * @returns - A promise that resolves with the API response.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_ai_model_settings_vadmin_invoice_ai_model_settings__model_id__put
 */
export const updateInvoiceAiModelSettings = async ({
  invoiceAiModelSettingsId,
  invoiceAiModelSettings,
}: {
  invoiceAiModelSettingsId: UUID;
  invoiceAiModelSettings: UpdateInvoiceAiModelSettingsPayload;
}) => {
  try {
    const response = await vestigasApi
      .put(
        ENDPOINT.INVOICE_AI_MODEL.SETTINGS.UPDATE(invoiceAiModelSettingsId),
        {
          json: invoiceAiModelSettings,
          validate: { body: zodUpdateInvoiceAiModelSettingsPayloadSchema },
        },
      )
      .json<UpdateInvoiceAiModelSettingsResponse>();

    return response;
  } catch (error) {
    log.error({
      message: 'Error updating invoice AI model settings',
      error,
    });

    // Re-throw the error to propagate it to the calling function.
    throw error; // re-throw error so it can be handled higher up in the callstack.
  }
};

/**
 * Custom hook for updating the invoice AI model settings with a given AI model settings ID using mutations.
 *
 * @see https://app.dev.vestigas.com/redoc#tag/VESTIGAS-Admin-Invoice/operation/update_invoice_ai_model_settings_vadmin_invoice_ai_model_settings__model_id__put
 */
export const useMutationUpdateInvoiceAiModelSettings = (
  options?: UseMutationOptions<
    UpdateInvoiceAiModelSettingsResponse,
    unknown,
    {
      invoiceAiModelSettingsId: UUID;
      invoiceAiModelSettings: UpdateInvoiceAiModelSettingsPayload;
    }
  >,
) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: async ({
      invoiceAiModelSettingsId,
      invoiceAiModelSettings,
    }: {
      invoiceAiModelSettingsId: UUID;
      invoiceAiModelSettings: UpdateInvoiceAiModelSettingsPayload;
    }) =>
      updateInvoiceAiModelSettings({
        invoiceAiModelSettingsId,
        invoiceAiModelSettings,
      }),
    onSettled(_data, _error, { invoiceAiModelSettingsId }) {
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getSettings(invoiceAiModelSettingsId),
      });
      void queryClient.invalidateQueries({
        queryKey: queryKeysInvoiceAiModel.getAllSettings({}),
      });
    },
    ...options,
  });
};
